/**
 * The console client of the app routes of shared libraries in release-engine
 * (2026-10-12-shared-libraries, WS-13, R-013, R-018, R-030, R-034).
 *
 * release-engine owns three app routes that the console calls:
 *
 * - `GET /v1/apps/:appId/library-rights` (WS-3): the approval keys that a consuming app or a consuming
 *   library holds, and every request it filed.
 * - `POST /v1/apps/:appId/library-rights/requests` (WS-3): a request for rights of one library version.
 * - `PUT /v1/apps/:appId/libraries/:librarySlug/config` (WS-2, R-013): the consumer owner's override of
 *   the `consumerConfig` values of one library instance.
 *
 * `libraryRightsQueue.api.ts` holds the queue and the decision, which `cp:library-rights:decide` gates. The two
 * halves live in two modules, so that each pane imports the module whose permission gates it.
 *
 * release-engine keeps its envelopes in `library-rights.contract.ts` and `set-consumer-config.dto.ts`,
 * outside every contract package. So this file copies each name and each field letter for letter, as
 * `kernelRights.api.ts` copies the kernel-rights envelopes. `libraryRights.wire-parity.test.ts` holds the
 * write bodies equal to `release-engine/openapi.json`.
 *
 * Who calls it: `AppLibrariesPane`, `LibraryRightsRequestForm` and `ConsumerConfigOverrideForm`.
 *
 * Rules that every call keeps:
 *
 * 1. A path id passes `encodeURIComponent`, so an id never splits a path.
 * 2. The request sends `stepUpHeaders(stepUpId)`: no step-up header for `null`, the header for an
 *    approved id, and a throw before any request for a malformed id. The override route takes no
 *    step-up.
 * 3. A write body holds the release-engine keys alone, because each schema is strict.
 * 4. A non-2xx answer throws `ApiError` with `status` and `body`; a network fault throws the raw
 *    failure. No call retries, caches or stores.
 *
 * The permission constants hide controls and decide nothing. The server decides every write.
 */
import { LIBRARY_SLUG_MAX_LENGTH, LIBRARY_SLUG_PATTERN, type CpPermission, type Sha256Digest } from '@zion/sdk-contracts';
import type { LibraryRightRequestStatus } from './libraryRightsQueue.api';
import { stepUpHeaders } from '../platform/auth/step-up';
import { api, apiFetch } from '../platform/http/client';

/** The read check of `GET /v1/apps/:appId/library-rights` and of the manifest entry `app-libraries`. */
export const LIBRARY_RIGHTS_READ_PERMISSION = 'cp:app:read' satisfies CpPermission;

/** The check of the request route, on the app: release-engine reuses the kernel-rights request permission (WS-3). */
export const LIBRARY_RIGHTS_REQUEST_PERMISSION = 'cp:app:kernel-rights:request' satisfies CpPermission;

/** The check of the override route, on the consuming app (R-013). */
export const CONSUMER_CONFIG_WRITE_PERMISSION = 'cp:app:write' satisfies CpPermission;

/** One approval key of a request: a library right name and its binding digest in one library version (R-018). */
export interface LibraryRightRequestEntry {
  /** `<library-slug>:<local name>`. */
  readonly name: string;
  /** The digest of the bound route set and inbound topics of that right in the named version (R-030 item 4). */
  readonly bindingDigest: Sha256Digest;
}

/** One library right request as release-engine shows it. Times are ISO 8601 UTC texts. */
export interface LibraryRightRequestView {
  readonly id: string;
  /** The consuming app or consuming library that asked. */
  readonly requesterAppId: string;
  /** The library row whose rights the request names. */
  readonly libraryAppId: string;
  readonly librarySlug: string;
  /** The approval keys of the request, in code-point order of the name. */
  readonly rights: readonly LibraryRightRequestEntry[];
  readonly justification: string;
  readonly status: LibraryRightRequestStatus;
  readonly requestedBy: string;
  readonly requestedAt: string;
  readonly decidedBy: string | null;
  readonly decidedAt: string | null;
  readonly decisionNote: string | null;
  readonly revokedBy: string | null;
  readonly revokedAt: string | null;
  readonly revokeNote: string | null;
}

/** One approval key that a requester holds: the library, the right name and the binding digest. */
export interface LibraryRightApprovalView {
  readonly libraryAppId: string;
  readonly librarySlug: string;
  readonly name: string;
  readonly bindingDigest: Sha256Digest;
  /** The approved request that holds the key. */
  readonly requestId: string;
}

/**
 * The library-rights page of one app or library: the approval keys it holds, sorted by library slug,
 * right name and digest, and every request it filed, newest first.
 */
export interface AppLibraryRightsView {
  readonly appId: string;
  readonly approved: readonly LibraryRightApprovalView[];
  readonly requests: readonly LibraryRightRequestView[];
}

/**
 * The body of `POST /v1/apps/:appId/library-rights/requests`: the library slug, one published version
 * of it, 1 to 32 right names and a justification of 20 to 2000 characters after the trim.
 */
export interface LibraryRightRequestBody {
  readonly library: string;
  readonly version: string;
  readonly rights: readonly string[];
  readonly justification: string;
}

/** The answer of the override route: the stored override of one instance. Times are ISO 8601 UTC texts. */
export interface ConsumerConfigOverrideView {
  /** `release.apps.id` of the consuming app. */
  readonly appId: string;
  /** The library slug of the route. */
  readonly librarySlug: string;
  /** `release.apps.id` of the instance row. */
  readonly instanceAppId: string;
  /** The stored override, or `null` when none is stored. */
  readonly config: Readonly<Record<string, unknown>> | null;
  /** The operator who wrote it, or `null` when the caller carried no operator id. */
  readonly updatedBy: string | null;
  readonly updatedAt: string;
}

/** `/v1/apps/<appId>/library-rights`, with the id encoded as one segment. Pure. */
export function appLibraryRightsPath(appId: string): string {
  return `/v1/apps/${encodeURIComponent(appId)}/library-rights`;
}

/**
 * Read the library-rights page of one app or library: `GET /v1/apps/:appId/library-rights`.
 *
 * @param appId the app UUID; release-engine refuses any other text with 400.
 * @throws ApiError on a non-2xx answer (403 with no `cp:app:read` on the app, 404 for an unknown app).
 */
export function getAppLibraryRights(appId: string): Promise<AppLibraryRightsView> {
  return api.get<AppLibraryRightsView>(appLibraryRightsPath(appId));
}

/**
 * Ask for rights of one library version: `POST /v1/apps/:appId/library-rights/requests` with
 * `{ library, version, rights, justification }`.
 *
 * @param stepUpId `null` on the first send; the approved step-up request id on the second.
 * @returns the new `pending` request (201).
 * @throws Error('invalid step-up request id') before any request for a malformed id; ApiError on a
 *   non-2xx answer: 409 `app_is_library_instance` for an instance row (`InstanceWriteRefusalInterceptor`,
 *   before the pipe), 400 from the pipe, 403 `step_up_required` on a first send, 403 for no
 *   `cp:app:kernel-rights:request`, 404 or 409 with a library-rights code (`library_not_found`,
 *   `library_not_used`, `libraries_not_accepted` and the others of `LibraryRightsRefusalCode`), 503
 *   `step_up_unavailable` or `code_signing_unavailable`.
 */
export async function requestLibraryRights(
  appId: string,
  body: LibraryRightRequestBody,
  stepUpId: string | null,
): Promise<LibraryRightRequestView> {
  const wire = { library: body.library, version: body.version, rights: body.rights, justification: body.justification };
  return apiFetch<LibraryRightRequestView>(`${appLibraryRightsPath(appId)}/requests`, {
    method: 'POST',
    body: JSON.stringify(wire),
    headers: stepUpHeaders(stepUpId),
  });
}

/**
 * `/v1/apps/<appId>/libraries/<librarySlug>/config`, with each segment encoded. Pure.
 *
 * @throws Error('invalid library slug') for a slug outside `LIBRARY_SLUG_PATTERN` or longer than
 *   `LIBRARY_SLUG_MAX_LENGTH` (30) characters, so the console never sends a path that the release-engine pipe refuses.
 */
export function consumerConfigPath(appId: string, librarySlug: string): string {
  if (librarySlug.length > LIBRARY_SLUG_MAX_LENGTH || !LIBRARY_SLUG_PATTERN.test(librarySlug)) throw new Error('invalid library slug');
  return `/v1/apps/${encodeURIComponent(appId)}/libraries/${librarySlug}/config`;
}

/**
 * Set (`config` an object) or remove (`config: null`) the consumer owner's override of one library
 * instance: `PUT /v1/apps/:appId/libraries/:librarySlug/config` with `{ config }`.
 *
 * @returns the stored override (200).
 * @throws Error('invalid library slug') before any request, as {@link consumerConfigPath}; ApiError on
 *   a non-2xx answer: 400 for a value that the library's `consumerConfig` schema refuses or for a
 *   consuming row of kind `library`, 403 for no `cp:app:write` on the app, 404 for an unknown app or
 *   library, or an app with no instance of the library, 409 `app_is_library_instance` for an instance
 *   row, 503 when the store fails.
 */
export async function setConsumerConfigOverride(
  appId: string,
  librarySlug: string,
  config: Readonly<Record<string, unknown>> | null,
): Promise<ConsumerConfigOverrideView> {
  return api.put<ConsumerConfigOverrideView>(consumerConfigPath(appId, librarySlug), { config });
}
